/**
 * Floor generation from a dungeon config and the `map` stream. The rules are
 * on the wiki page Architecture → game-core contract, section Floor
 * generation. Every draw goes through rng.ts on the stream passed in, and the
 * advanced stream is returned; nothing here reads any other randomness.
 */
import type { ContentPack, Dungeon } from '@ab/content-schema';
import { lookup } from './content.ts';
import { inRoom, roomCenter, tilesOfRoom } from './floorview.ts';
import { type Pos, comparePos, manhattan, samePos } from './grid.ts';
import { type RngState, between, nextInt, pickWeighted } from './rng.ts';
import { type BossScaling, type Floor, type FloorItem, type FloorSpring, InvalidCommand, type Room, type Tile, startingSkills } from './state.ts';

/** Attempts at placing a random room in one placement round. */
const placementAttempts = 200;
/** Placement rounds (each starting from an empty grid) before settling for the best round. */
const placementRounds = 10;

/** An enemy as generated, before it gets a unit id. */
export type GeneratedEnemy = {
  readonly monster: string;
  readonly level: number;
  readonly passive: string;
  readonly skills: readonly string[];
  readonly pos: Pos;
  /** The dungeon's boss scaling for the boss; null for ordinary enemies. */
  readonly boss: BossScaling | null;
};

export type GeneratedFloor = {
  /** `floor.items` holds the same list as `items`. */
  readonly floor: Floor;
  readonly rng: RngState;
  /** One tile per party member, in party order (the leader first). */
  readonly partyTiles: readonly Pos[];
  readonly enemies: readonly GeneratedEnemy[];
  readonly items: readonly FloorItem[];
};

/** Draws thread the stream through this cursor so the helpers stay short. */
type Cursor = { rng: RngState };

const draw = (c: Cursor, min: number, max: number): number => {
  const r = between(c.rng, min, max);
  c.rng = r.next;
  return r.value;
};

const drawIndex = (c: Cursor, length: number): number => {
  const r = nextInt(c.rng, length);
  c.rng = r.next;
  return r.value;
};

const drawWeighted = (c: Cursor, weights: readonly number[]): number => {
  const r = pickWeighted(c.rng, weights);
  c.rng = r.next;
  return r.index;
};

/** Rooms keep one wall tile between each other and from the border. */
const fits = (rooms: readonly Room[], r: Room): boolean =>
  rooms.every((o) => r.x + r.w < o.x || o.x + o.w < r.x || r.y + r.h < o.y || o.y + o.h < r.y);

const center = roomCenter;

/**
 * Draws the room count, then places rooms in rounds: each round starts from an
 * empty grid and makes up to `placementAttempts` random draws. The first round
 * that reaches the count wins; after `placementRounds` rounds, the round with
 * the most rooms (the earliest on a tie). Fewer than `rooms.count.min` rooms
 * throws: the config does not fit the grid (content lint bounds make this rare).
 */
const placeRooms = (c: Cursor, dungeon: Dungeon): Room[] => {
  const { width, height } = dungeon.grid;
  const count = draw(c, dungeon.rooms.count.min, dungeon.rooms.count.max);
  const sizeMin = Math.max(1, dungeon.rooms.size.min);
  const sizeMax = Math.max(sizeMin, dungeon.rooms.size.max);
  let best: Room[] = [];
  for (let round = 0; round < placementRounds; round += 1) {
    const rooms: Room[] = [];
    for (let i = 0; i < placementAttempts && rooms.length < count; i += 1) {
      const w = Math.min(draw(c, sizeMin, sizeMax), width - 2);
      const h = Math.min(draw(c, sizeMin, sizeMax), height - 2);
      const room = { x: draw(c, 1, width - 1 - w), y: draw(c, 1, height - 1 - h), w, h };
      if (fits(rooms, room)) rooms.push(room);
    }
    if (rooms.length >= count) return rooms;
    if (rooms.length > best.length) best = rooms;
  }
  if (best.length < Math.max(1, dungeon.rooms.count.min)) throw new InvalidCommand('rooms_do_not_fit', `dungeon ${dungeon.id} cannot fit its rooms on this grid`, { dungeon: dungeon.id });
  return best;
};

const carve = (tiles: Tile[], width: number, p: Pos): void => {
  tiles[p.y * width + p.x] = 'floor';
};

/** An L-shaped corridor between two room centres; a coin from the stream picks which leg comes first. */
const corridor = (c: Cursor, tiles: Tile[], width: number, a: Room, b: Room): void => {
  const from = center(a);
  const to = center(b);
  const horizontalFirst = drawIndex(c, 2) === 0;
  const corner = horizontalFirst ? { x: to.x, y: from.y } : { x: from.x, y: to.y };
  for (const [p, q] of [[from, corner], [corner, to]] as const) {
    const sx = Math.sign(q.x - p.x);
    const sy = Math.sign(q.y - p.y);
    let cur = p;
    carve(tiles, width, cur);
    while (cur.x !== q.x || cur.y !== q.y) {
      cur = { x: cur.x + sx, y: cur.y + sy };
      carve(tiles, width, cur);
    }
  }
};

const layTiles = (c: Cursor, dungeon: Dungeon, rooms: readonly Room[]): Tile[] => {
  const { width, height } = dungeon.grid;
  const tiles: Tile[] = [];
  for (let i = 0; i < width * height; i += 1) tiles.push('wall');
  for (const r of rooms) for (const p of tilesOfRoom(r)) carve(tiles, width, p);
  for (let i = 0; i + 1 < rooms.length; i += 1) {
    const a = rooms[i];
    const b = rooms[i + 1];
    if (a && b) corridor(c, tiles, width, a, b);
  }
  if (rooms.length >= 3) {
    const i = drawIndex(c, rooms.length);
    const j0 = drawIndex(c, rooms.length - 1);
    const j = j0 >= i ? j0 + 1 : j0;
    const a = rooms[i];
    const b = rooms[j];
    if (a && b) corridor(c, tiles, width, a, b);
  }
  return tiles;
};

/** The room farthest from room 0 by room-centre Manhattan distance; ties go to the lower index. */
const farthestRoom = (rooms: readonly Room[]): number => {
  const start = rooms[0];
  if (!start) return 0;
  let best = 0;
  let bestDist = -1;
  rooms.forEach((r, i) => {
    const d = manhattan(center(start), center(r));
    if (d > bestDist) {
      best = i;
      bestDist = d;
    }
  });
  return best;
};

/** Takes a random tile out of `free` (mutating the local list) or returns null when none is left. */
const takeTile = (c: Cursor, free: Pos[]): Pos | null => {
  if (free.length === 0) return null;
  const i = drawIndex(c, free.length);
  const [p] = free.splice(i, 1);
  return p ?? null;
};

const enemyAt = (c: Cursor, content: ContentPack, dungeon: Dungeon, floorNumber: number, pos: Pos): GeneratedEnemy => {
  const entry = dungeon.enemies[drawWeighted(c, dungeon.enemies.map((e) => e.weight))];
  if (!entry) throw new Error('mapgen: empty enemy table');
  const species = lookup(content.monsters, entry.monster);
  if (species === null) throw new Error(`mapgen: unknown monster ${entry.monster}`);
  const rolled = draw(c, entry.level.min, entry.level.max) + dungeon.levelPerFloor * (floorNumber - 1);
  const level = Math.max(1, Math.min(content.rules.combat.levelCap, rolled));
  const passive = species.passivePool[drawIndex(c, species.passivePool.length)] ?? '';
  return { monster: entry.monster, level, passive, skills: startingSkills(content, entry.monster, level), pos, boss: null };
};

/**
 * The last floor's boss: the dungeon's `boss` species at exactly its level
 * (clamped to 1..`levelCap`; content lint refuses a level above the cap),
 * on a random free tile of the exit room (any free tile on a one-room floor;
 * the exit tile and party tiles are never free), then a random passive from
 * its pool and its learnset skills for that level. Throws when no tile is
 * free: there is no fallback placement.
 */
const placeBoss = (c: Cursor, content: ContentPack, dungeon: Dungeon, exitRoom: Room, oneRoom: boolean, free: Pos[]): GeneratedEnemy => {
  const candidates = oneRoom ? free : free.filter((p) => inRoom(exitRoom, p));
  if (candidates.length === 0) throw new InvalidCommand('no_tile_for_boss', `dungeon ${dungeon.id} has no free tile for its boss`, { dungeon: dungeon.id });
  const pos = candidates[drawIndex(c, candidates.length)];
  if (!pos) throw new Error('mapgen: boss tile out of range');
  free.splice(free.indexOf(pos), 1);
  const { monster, hpPercent, statPercent } = dungeon.boss;
  const level = Math.max(1, Math.min(content.rules.combat.levelCap, dungeon.boss.level));
  const species = lookup(content.monsters, monster);
  if (species === null) throw new Error(`mapgen: unknown monster ${monster}`);
  const passive = species.passivePool[drawIndex(c, species.passivePool.length)] ?? '';
  return { monster, level, passive, skills: startingSkills(content, monster, level), pos, boss: { hpPercent, statPercent } };
};

/**
 * Party tiles: floor tiles (never the exit) of the start room first, then the
 * rest; each group by Manhattan distance from the start room's centre, then
 * row by row. One per party member; throws when the floor has too few.
 */
const choosePartyTiles = (tiles: readonly Tile[], width: number, height: number, start: Room, partySize: number): Pos[] => {
  const centre = roomCenter(start);
  const outside = (p: Pos): number => (inRoom(start, p) ? 0 : 1);
  const open: Pos[] = [];
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) if (tiles[y * width + x] === 'floor') open.push({ x, y });
  open.sort((a, b) => outside(a) - outside(b) || manhattan(a, centre) - manhattan(b, centre) || comparePos(a, b));
  if (open.length < partySize) throw new InvalidCommand('no_room_for_party', 'no room for the party');
  return open.slice(0, partySize);
};

/** Enemies: the drawn count, capped at the free tiles left after the springs. */
const placeEnemies = (c: Cursor, content: ContentPack, dungeon: Dungeon, floorNumber: number, free: Pos[]): GeneratedEnemy[] => {
  const count = Math.min(draw(c, dungeon.enemiesPerFloor.min, dungeon.enemiesPerFloor.max), free.length);
  const out: GeneratedEnemy[] = [];
  for (let i = 0; i < count; i += 1) {
    const pos = takeTile(c, free);
    if (pos === null) break;
    out.push(enemyAt(c, content, dungeon, floorNumber, pos));
  }
  return out;
};

/** Items: the drawn count, capped at the free tiles left after the enemies. */
const placeItems = (c: Cursor, dungeon: Dungeon, free: Pos[]): FloorItem[] => {
  if (dungeon.items.length === 0) return [];
  const count = Math.min(draw(c, dungeon.itemsPerFloor.min, dungeon.itemsPerFloor.max), free.length);
  const out: FloorItem[] = [];
  for (let i = 0; i < count; i += 1) {
    const pos = takeTile(c, free);
    if (pos === null) break;
    const entry = dungeon.items[drawWeighted(c, dungeon.items.map((e) => e.weight))];
    if (entry) out.push({ pos, item: entry.item, count: 1 });
  }
  return out;
};

/**
 * AP springs, before the enemies and items so that a crowded floor still has
 * them: throws when fewer free tiles than `springsPerFloor.min` are left, else
 * the count drawn from `springsPerFloor`, capped at the free tiles; each on a
 * random free tile, its percent drawn by weight from `springs`. Returned in
 * row-major order.
 */
const placeSprings = (c: Cursor, dungeon: Dungeon, free: Pos[]): FloorSpring[] => {
  if (free.length < dungeon.springsPerFloor.min) throw new InvalidCommand('no_room_for_springs', `dungeon ${dungeon.id} has no room for its springs`, { dungeon: dungeon.id });
  const count = Math.min(draw(c, dungeon.springsPerFloor.min, dungeon.springsPerFloor.max), free.length);
  const out: FloorSpring[] = [];
  for (let i = 0; i < count; i += 1) {
    const pos = takeTile(c, free);
    if (pos === null) break;
    const entry = dungeon.springs[drawWeighted(c, dungeon.springs.map((e) => e.weight))];
    if (entry) out.push({ pos, percent: entry.percent });
  }
  return out.sort((a, b) => comparePos(a.pos, b.pos));
};

/**
 * Generates floor `floorNumber` (1-based) of `dungeon` for a party of
 * `partySize`. Rooms are placed at random with a one-tile margin (`placeRooms`),
 * joined in placement order by L-shaped corridors plus one extra corridor
 * when there are three or more. The party starts in room 0; the exit is a
 * random tile of the room farthest from it (room 0 itself on a one-room
 * floor), except on the last floor, which has no exit tile (that room still
 * holds the boss). The party's tiles are chosen next (no draws), then on the
 * last floor the boss (`placeBoss`), then AP springs (`placeSprings`, which
 * throws when the floor has too few free tiles for `springsPerFloor.min`),
 * then enemies, then items, on distinct random free tiles: floor tiles that are not
 * the exit, not a party tile, and outside the start room unless the floor has
 * one room.
 */
export const generateFloor = (content: ContentPack, dungeon: Dungeon, floorNumber: number, partySize: number, mapRng: RngState): GeneratedFloor => {
  const c: Cursor = { rng: mapRng };
  const { width, height } = dungeon.grid;
  const rooms = placeRooms(c, dungeon);
  const tiles = layTiles(c, dungeon, rooms);
  const exitRoom = farthestRoom(rooms);
  const exitRect = rooms[exitRoom];
  if (!exitRect) throw new Error('mapgen: no exit room');
  const last = floorNumber === dungeon.floors;
  if (!last) {
    // The last floor has no exit: defeating its boss ends the run.
    const exitTiles = tilesOfRoom(exitRect);
    const exit = exitTiles[drawIndex(c, exitTiles.length)] ?? center(exitRect);
    tiles[exit.y * width + exit.x] = 'exit';
  }

  const start = rooms[0];
  if (!start) throw new Error('mapgen: no start room');
  const partyTiles = choosePartyTiles(tiles, width, height, start, partySize);
  const startExcluded = rooms.length > 1;
  const free: Pos[] = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const p = { x, y };
      if (tiles[y * width + x] !== 'floor' || partyTiles.some((q) => samePos(p, q))) continue;
      if (startExcluded && inRoom(start, p)) continue;
      free.push(p);
    }
  }
  const boss = last ? [placeBoss(c, content, dungeon, exitRect, rooms.length === 1, free)] : [];
  const springs = placeSprings(c, dungeon, free);
  const enemies = [...boss, ...placeEnemies(c, content, dungeon, floorNumber, free)];
  const items = placeItems(c, dungeon, free);
  const floor: Floor = { width, height, tiles, rooms, items, springs, startRoom: 0, exitRoom };
  return { floor, rng: c.rng, partyTiles, enemies, items };
};
